import React from "react";
import { motion } from "framer-motion";
import { THEME } from "./theme";

// The "Select a day" grid. The cells are the app's own Batch Operations day
// cells — `.days` in App.css supplies the lavender fill, the orange today
// cell and the navy selected cell — so the two screens can't drift apart.
//
// Completeness is layered on top as a RING rather than a fill, because the
// fill is already carrying "today / selected / everything else":
//   green ring = all 3 logged, amber ring = partly logged, no ring = nothing.
// Rings come from the bulk status API; when it is unavailable every cell is
// plain and the legend is hidden.

const RING = { full: THEME.green, partial: "#f59e0b" };

const LegendDot = ({ color, label, ring = true }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
    <span
      style={{
        width: 12,
        height: 12,
        borderRadius: "50%",
        background: "#d2d0ee", // same lavender as a day cell
        boxShadow: ring ? `0 0 0 2px ${color}` : "none",
        border: ring ? "none" : `1px solid ${color}`,
        boxSizing: "border-box",
      }}
    />
    {label}
  </span>
);

const DayGrid = ({ days, statusMap, statusOk, selectedDate, note, t, onPick }) => (
  <div>
    {days.length > 0 && (
      <ul
        className="days"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(8, 1fr)",
          gap: "14px 6px",
          marginTop: 22,
          marginBottom: 4,
          padding: 0,
          listStyle: "none",
          overflow: "visible", // the rings are box-shadows, they must not clip
        }}
      >
        {days.map((it, idx) => {
          const st = statusOk ? statusMap?.get(it.value) : null;
          const ring = st && st.complete !== "none" ? RING[st.complete] : null;
          return (
            <motion.li
              key={it.value}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: Math.min(idx * 0.015, 0.5) }}
              // Fill classes from App.css; `green-bg` is the plain lavender
              // default (the class carries no rule of its own).
              className={it.isToday ? "today-bg" : it.value === selectedDate ? "active" : "green-bg"}
              style={{
                position: "relative",
                float: "none", // .days li floats; the grid places them instead
                margin: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
              title={`${t.dayPrefix} ${it.day} · ${it.value}`}
            >
              {/* An <a> because App.css styles the cell as `.days li a`; it
                  behaves as a button, so it carries the role and key handling
                  rather than an href. */}
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <a
                role="button"
                tabIndex={0}
                onClick={() => onPick(it)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onPick(it);
                  }
                }}
                style={ring ? { boxShadow: `0 0 0 2px ${ring}` } : undefined}
              >
                {it.day}
              </a>
              {it.doc && (
                <span
                  style={{
                    position: "absolute",
                    top: -18,
                    left: 0,
                    right: 0,
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: 11,
                    color: "#64748b",
                  }}
                >
                  DOC
                </span>
              )}
            </motion.li>
          );
        })}
      </ul>
    )}

    {note && (
      <div
        style={{
          margin: "12px 2px 2px",
          padding: "8px 12px",
          background: note.done ? "#dcfce7" : THEME.soft,
          borderRadius: 10,
          fontSize: 12.5,
          fontWeight: 600,
          color: note.done ? THEME.green : THEME.blue,
          textAlign: "center",
        }}
      >
        {note.text}
      </div>
    )}

    {statusOk && (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          margin: "10px 2px 2px",
          fontSize: 11,
          color: "#64748b",
        }}
      >
        <LegendDot color={RING.full} label={t.legendComplete} />
        <LegendDot color={RING.partial} label={t.legendPartial} />
        <LegendDot color="#cbd5e1" label={t.legendEmpty} ring={false} />
      </div>
    )}
  </div>
);

export default DayGrid;
